import { Box, Fade, Flex } from '@chakra-ui/react';
import React from 'react';

import { isLegacyBrowser } from '../../utils/browser.utils';
import Glass from '../Glass';
import { Text } from '../Text';
import ActionSection from './ActionSection';
import Navigation from './Navigation';
import {
  NavigationContextProvider,
  useNavigation,
} from './Navigation/Navigation.context';
import Notification from './Notification';
import { NavData } from './types';

interface HeaderProps {
  navData: NavData;
  notifications?: React.ReactElement[];
}

const ObsoleteBrowserWarning: React.FC = () => (
  <Text>
    Hello, we noticed you are using Internet Explorer (IE). Please switch to a
    modern browser (e.g. Google Chrome) for a better experience. Otherwise, you
    may notice slow loading times and features working incorrectly.
  </Text>
);

const DesktopGlass: React.FC = () => {
  const { main } = useNavigation();
  if (!main.active) {
    return null;
  }
  return (
    <Fade in>
      <Glass
        display={{ base: 'none', laptop: 'block' }}
        className="Glass_Desktop"
        onClick={main.close}
      />
    </Fade>
  );
};

const displayNavBar = (hasNotifications: boolean) => {
  if (hasNotifications) {
    return {
      base: 'block',
      laptop: 'block',
    };
  }
  return {
    base: 'none',
    laptop: 'block',
  };
};

const getHeaderSpacerHeight = (hasNotifications: boolean) => {
  if (hasNotifications) {
    return {
      base: '177px',
      md: '161px',
      laptop: '129px',
    };
  }
  return {
    base: '113px',
    md: '113px',
    laptop: '129px',
  };
};

const Header: React.FC<HeaderProps> = ({ navData, notifications = [] }) => {
  const messages = React.useMemo(() => {
    const result = [...notifications];
    if (isLegacyBrowser()) {
      result.unshift(<ObsoleteBrowserWarning />);
    }
    return result;
  }, [notifications]);

  return (
    <>
      <Box
        as="header"
        className="WLHeader"
        position="fixed"
        left={0}
        top={0}
        zIndex={10}
        width="100%"
        backgroundColor="white"
        sx={{
          '@media screen and (max-height: 400px)': {
            position: 'static',
          },
        }}
      >
        <Box
          as="nav"
          className="WLHeader-SecondaryNavBar"
          display={displayNavBar(messages.length > 0)}
          position="relative"
          // left="0"
          // top="0"
          // width="100%"
          zIndex={{ base: 0, laptop: 11 }}
          height={{ base: '64px', md: '48px' }}
          // TODO: Use color vars instead of rgba values. -NE 20220315
          // rgb(0, 51, 102) = wl-darkBlue
          // rgb(102, 153, 204) = wl-lightBlue
          background="linear-gradient(to right, rgb(0, 51, 102), rgb(102, 153, 204))"
        >
          <Flex
            mx="auto"
            paddingRight={{ lg: '16px' }}
            // TODO: Use 2xl size var. -NE 20220316
            maxW="1536px"
            height="100%"
          >
            <Notification>{messages}</Notification>
            <Box
              display={{ base: 'none', laptop: 'flex' }}
              flexBasis="auto"
              flexGrow="0"
              flexShrink="0"
              marginLeft="auto"
            >
              <ActionSection />
            </Box>
          </Flex>
        </Box>
        <NavigationContextProvider data={navData}>
          <Navigation data={navData} />
          <DesktopGlass />
        </NavigationContextProvider>
      </Box>
      <Box
        height={getHeaderSpacerHeight(messages.length > 0)}
        width="100%"
      ></Box>
    </>
  );
};

export default Header;
